import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { useTheme } from '@mui/material/styles';
import { adjustTripDepartureTime } from '../../../helpers/api/rides';
import { useToast } from '../../../hooks';

// The value format of <input type="datetime-local">. Deliberately not parsed
// with the bare Date constructor anywhere in this file: the string carries no
// offset, so `new Date(value)` would read it in the BROWSER's zone. Ops works
// in the trip's origin city, so every conversion goes through moment.tz.
const LOCAL_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

const makeInputStyle = (theme) => ({
  width: '100%',
  padding: '12px 14px',
  fontSize: '16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
});

/** Wall-clock string in `timeZone` for an instant. Empty when the zone is missing. */
const toLocalInputValue = (instant, timeZone) => {
  if (!timeZone) return '';
  return moment.utc(instant).tz(timeZone).format(LOCAL_INPUT_FORMAT);
};

/** The instant that a wall-clock string denotes in `timeZone`. */
const fromLocalInputValue = (value, timeZone) =>
  moment.tz(value, LOCAL_INPUT_FORMAT, timeZone);

const AdjustTripTimeDialog = ({ open, onClose, trip, onSuccess }) => {
  const theme = useTheme();
  const inputStyle = makeInputStyle(theme);
  const t = useToast();
  // Some trips' origin city has no configured time zone. Every conversion
  // below depends on it, so hooks are still called unconditionally (React
  // forbids conditional hooks) but produce inert values, and the render
  // falls back to an error state further down.
  const timeZone = trip.originCity?.timeZone;
  const hasTimeZone = Boolean(timeZone);

  // `toLocalInputValue` itself already guards a missing zone, but seed via
  // `hasTimeZone` explicitly too: it's the single flag every other value
  // below is gated on, so the missing-timezone error shell stays reachable
  // even if that inner guard is ever weakened.
  const currentValue = useMemo(
    () => (hasTimeZone ? toLocalInputValue(trip.departureTime, timeZone) : ''),
    [hasTimeZone, trip.departureTime, timeZone],
  );

  const [value, setValue] = useState(currentValue);
  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState(null);
  // Whether the user has typed into the input since the dialog last opened.
  // Drives whether an incoming `currentValue` change (the trip prop
  // refetching under us) is safe to reseed from, or would clobber typing.
  const [edited, setEdited] = useState(false);
  // Set when trip.departureTime changes while `edited` is true, so the
  // dialog can tell the user their preview now reflects a new baseline
  // instead of silently recomputing out from under them.
  const [movedWhileEditing, setMovedWhileEditing] = useState(false);

  // Reset whenever the dialog reopens, so a cancelled edit (and any
  // edited/moved tracking from a prior open) doesn't persist. Deliberately
  // keyed on `open` alone, not `currentValue` -- see the effect below for why
  // a later `currentValue` change while already open is handled separately.
  useEffect(() => {
    if (open) {
      setValue(currentValue);
      setWarnings(null);
      setSubmitting(false);
      setEdited(false);
      setMovedWhileEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // trip.departureTime can change while the dialog is already open: a
  // scheduler job nudges undriven trips' departureTime every ~15 minutes,
  // and TripDetailsDialog also refetches the full trip as this dialog opens.
  // If the user hasn't typed anything yet, reseed so the preview reflects
  // the new value. If they have, don't clobber their typing -- just flag
  // that the ground shifted under them.
  useEffect(() => {
    if (!open) return;
    if (edited) {
      setMovedWhileEditing(true);
      return;
    }
    setValue(currentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  const target = hasTimeZone ? fromLocalInputValue(value, timeZone) : null;
  const isValid = hasTimeZone && target.isValid();
  // Float minutes: trip.departureTime can carry non-zero seconds, and a
  // truncating diff (moment's default) can round a genuine change down to 0,
  // wrongly leaving `changed` false. Only the human-readable text below
  // rounds.
  const deltaMinutes = isValid
    ? target.diff(moment.utc(trip.departureTime), 'minutes', true)
    : 0;
  // "Changed" means "the user edited the input" — and at the input's own
  // minute resolution, that's exactly `value !== currentValue`. Don't swap
  // this for a diff against trip.departureTime: the seed itself is produced
  // by truncating trip.departureTime to the minute (toLocalInputValue), so
  // if the stored departure carries seconds, re-parsing the untouched seed
  // would show a spurious sub-minute delta and Apply would go live on a
  // no-op. deltaMinutes above still reflects the true instant-to-instant
  // shift and stays correct for the preview text and per-ride arithmetic.
  const changed = isValid && value !== currentValue;

  // moment.tz normalizes a wall-clock time that falls in a DST spring-forward
  // gap (e.g. 2:30 AM on the day clocks skip 2:00-3:00 AM) to the next valid
  // instant instead of rejecting it. Round-tripping the parsed instant back
  // through the same format/zone catches that silently: if it doesn't match
  // what was typed, the typed wall-clock time never existed in this zone.
  const dstGapDetected = isValid && target.format(LOCAL_INPUT_FORMAT) !== value;

  const submit = async (overrideWarnings) => {
    if (!changed) return;
    setSubmitting(true);
    try {
      const response = await adjustTripDepartureTime(trip.id, {
        departureTime: target.toISOString(),
        overrideWarnings,
        // What the client believed trip.departureTime was when it computed
        // this preview/delta. The server can compare this against its own
        // current value and warn (STALE_TRIP) if they no longer match --
        // it doesn't read this yet, but the DTO tolerates unknown fields.
        expectedDepartureTime: moment.utc(trip.departureTime).toISOString(),
      });
      const result = response.data;
      if (result.updated) {
        t.success('Trip time updated');
        onSuccess?.();
        onClose();
        return;
      }
      if (result.warnings?.length > 0) {
        setWarnings(result.warnings);
        setSubmitting(false);
        return;
      }
      // updated: false with no warnings is the server's zero-delta no-op
      // response, not a failure — there's nothing to override, but nothing
      // changed either, so don't claim success.
      t.info('No change applied');
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update trip time';
      t.error(message);
      setSubmitting(false);
    }
  };

  const describeDelta = () => {
    const roundedDelta = Math.round(deltaMinutes);
    const abs = Math.abs(roundedDelta);
    const hours = Math.floor(abs / 60);
    const mins = abs % 60;
    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (mins || !hours) parts.push(`${mins}m`);
    return `${parts.join(' ')} ${roundedDelta > 0 ? 'later' : 'earlier'}`;
  };

  // No origin time zone means we can't safely convert anything below — every
  // moment() call above would otherwise chain off an undefined zone and
  // throw. Render a minimal shell with just an explanation and a way out.
  if (!hasTimeZone) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box display="flex" alignItems="center" justifyContent="space-between" px="24px" pt="24px">
          <DialogTitle sx={{ padding: 0 }}>Adjust Trip Time</DialogTitle>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          <Alert severity="error">
            This trip&apos;s origin city has no configured time zone, so its departure
            time can&apos;t be reliably edited here. Fix the origin city&apos;s time zone first.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: '24px', pb: '24px' }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box display="flex" alignItems="center" justifyContent="space-between" px="24px" pt="24px">
        <DialogTitle sx={{ padding: 0 }}>Adjust Trip Time</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack spacing="16px">
          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              Current departure ({trip.originCity.cityName} time)
            </Typography>
            <Typography>
              {moment.utc(trip.departureTime).tz(timeZone).format('MMM DD, YYYY hh:mm A z')}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" mb="4px">
              New departure ({trip.originCity.cityName} time)
            </Typography>
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setWarnings(null);
                setEdited(true);
              }}
              style={inputStyle}
            />
          </Box>

          {movedWhileEditing && (
            <Alert severity="warning">
              This trip&apos;s departure changed to{' '}
              {moment.utc(trip.departureTime).tz(timeZone).format('MMM DD, YYYY hh:mm A z')}{' '}
              while you were editing. The preview below reflects the new time.
            </Alert>
          )}

          {dstGapDetected && (
            <Alert severity="warning">
              {value} does not exist in {trip.originCity.cityName}&apos;s time zone — daylight
              saving moves the clock forward that day. Applying this will actually submit{' '}
              {target.format('MMM DD, YYYY hh:mm A z')}.
            </Alert>
          )}

          {changed && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb="4px">
                Every ride on this trip moves {describeDelta()}
              </Typography>
              {trip.rides.map((ride) => {
                const rideId = ride.id || ride.rideId;
                return (
                  <Typography key={rideId} variant="body2" color="text.secondary">
                    {moment.utc(ride.departureTime).tz(timeZone).format('hh:mm A')}
                    {' → '}
                    {moment
                      .utc(ride.departureTime)
                      .add(Math.round(deltaMinutes), 'minutes')
                      .tz(timeZone)
                      .format('hh:mm A z')}
                  </Typography>
                );
              })}
            </Box>
          )}

          {warnings && warnings.length > 0 && (
            <Stack spacing="8px">
              {warnings.map((w) => (
                <Alert key={w.code} severity="warning">
                  <Typography variant="body2" fontWeight={700}>{w.code}</Typography>
                  <Typography variant="body2">{w.message}</Typography>
                </Alert>
              ))}
            </Stack>
          )}

          <Typography variant="body2" color="text.secondary">
            Riders are not notified. Contact them separately if the change affects them.
          </Typography>
          {(trip.nextTrip || trip.previousTrip) && (
            <Typography variant="body2" color="text.secondary">
              This trip&apos;s {trip.nextTrip ? 'return' : 'outbound'} leg keeps its current
              time. Adjust it separately if it also needs to change.
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: '24px', pb: '24px' }}>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        {warnings ? (
          <Button
            variant="contained"
            color="warning"
            disabled={submitting}
            onClick={() => submit(true)}
          >
            {submitting ? <CircularProgress size={20} /> : 'Override warnings and apply'}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!changed || submitting}
            onClick={() => submit(false)}
          >
            {submitting ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdjustTripTimeDialog;
